/**
 * Versions diff API — typed calls over the shared axios instance
 * (contracts-plan.md Appendix A "Versions", §13.8 WP-74; §4.5, §5.4, §5.5,
 * §6.8 G9, R32, R34).
 *
 * Never imports axios directly — only `services/api`, the same seam every
 * other module under `frontend/src/contracts/api/` uses.
 *
 * NAME CHECK (session 4 FACTS 9, the MOUNT-1 "duplicate NegotiationKind"
 * lesson): every name this file exports was checked against `client.ts`,
 * `rateCard.ts`, `notes.ts` and `workspace.ts` before it was written here —
 * none of `ParseInventory`, `VersionWithInventory`,
 * `listVersionsWithInventory`, `VersionDiffResult`, `BudgetLineDiffRow`,
 * `BudgetCellDiffRow`, `ClauseDiffRow`, `getVersionDiff`,
 * `RedlineDiffSummary`, `getContractRedlineDiff`, `markVersionExecuted`,
 * `supersedeVersion` or `describeVersionsApiError` is declared anywhere in
 * those four files. `api/workspace.ts` (WP-55, landed) already declares
 * `ContractsVersionDirection`, `ContractsVersionSource`,
 * `ContractsParseStatus`, `ContractsVersion`, `VersionDownload`,
 * `listNegotiationVersions`, `getVersion` and `downloadVersion` — this file
 * imports those, never redeclares them, and adds only what is missing.
 */
import api from '../../services/api';
import type { ContractsVersion } from './workspace';

// Imported for this file's OWN internal use only (VersionWithInventory below
// extends ContractsVersion) — never re-exported. `workspace.ts` already
// declares ContractsVersion, ContractsVersionDirection,
// ContractsVersionSource, ContractsParseStatus, VersionDownload,
// listNegotiationVersions, getVersion and downloadVersion (session 4 FACTS
// 9); the panel imports those directly from `api/workspace`, and this file
// only.

// ---------------------------------------------------------------------------
// The coverage inventory (§6.8, G9) — read defensively. Every parser's own
// inventory shape differs (xlsx: {reader, interpret}; docx: {reader,
// interpret: {clauses}}; pdf: {reader, execution, interpret?}), so this type
// only fixes what every run shares; the panel walks the rest with optional
// chaining and renders anything it does not recognise as a plain key/value
// line rather than dropping it (this WP's brief item 7).
// ---------------------------------------------------------------------------

export type ParseRunStatus = 'running' | 'parsed' | 'failed' | 'unsupported';
export type G8Label = 'extracted' | 'derived' | 'inferred';

export interface ParseInventory {
  parseRunId: string;
  status: ParseRunStatus;
  parser: string | null;
  templateFamily: string | null;
  familyLabel: G8Label | null;
  /** Parser-shaped (reader/interpret/execution); read it defensively (G9). */
  inventory: Record<string, unknown> | null;
  errors: unknown[] | null;
  startedAt: string;
  finishedAt: string | null;
}

/** GET /negotiations/:id/versions's actual row shape — ContractsVersion PLUS the latest parse run's inventory (workspace.ts's own type omits this). */
export interface VersionWithInventory extends ContractsVersion {
  parseInventory: ParseInventory | null;
}

/** GET /negotiations/:id/versions, WITH parseInventory (the same mounted route api/workspace.ts's listNegotiationVersions also reads, minus this one field). */
export async function listVersionsWithInventory(negotiationId: string): Promise<VersionWithInventory[]> {
  const response = await api.get<{ versions: VersionWithInventory[] }>(
    `/api/contracts/negotiations/${negotiationId}/versions`
  );
  return response.data?.versions ?? [];
}

// ---------------------------------------------------------------------------
// The JSON diff — GET /versions/:a/diff/:b (this WP's own route; session 4
// FACTS 3 is the exact contract). Distinct from WP-64's GET /diff?a&b, which
// answers with the Word tracked-changes document itself (see
// getContractRedlineDiff below).
// ---------------------------------------------------------------------------

export type VersionDiffKind = 'budget' | 'contract';

export interface VersionDiffSummarySide {
  id: string;
  filename: string;
  version_no: number;
  direction: string;
  round: number | null;
  version_date: string;
  parse_run_id: string | null;
  parser: string | null;
}

/** Exactly the six §5-preamble provenance keys (G7). */
export interface DiffProvenance {
  file: string | null;
  version_id: string | null;
  sheet: string | null;
  cell: string | null;
  section: string | null;
  page: string | number | null;
}

export interface DiffNumberPair {
  a: number | null;
  b: number | null;
  delta: number | null;
  delta_pct: number | null;
}

export interface DiffOverheadPair {
  a: number | null;
  b: number | null;
  delta_points: number | null;
}

/** One row of `lines.changed` / `lines.added` / `lines.removed` (§13.8 FACTS 4). */
export interface BudgetLineDiffRow {
  entry_id: string | null;
  section: string | null;
  matched_by: 'entry_id' | 'label_section' | null;
  label: { a: string | null; b: string | null };
  sheet: { a: string | null; b: string | null };
  row: { a: number | null; b: number | null };
  unit_cost: DiffNumberPair;
  line_total: DiffNumberPair;
  overhead_pct: DiffOverheadPair | null;
  line_ids: { a: Array<string | number>; b: Array<string | number> };
  provenance: { a: DiffProvenance; b: DiffProvenance };
  labels: { a: G8Label | null; b: G8Label | null; delta: 'derived' | 'inferred' | null };
}

export interface SkippedBudgetLine {
  side: 'a' | 'b';
  section: string | null;
  label_raw: string | null;
  sheet: string | null;
  row: number | null;
  reason: string;
  provenance: DiffProvenance;
}

export interface BudgetLinesDiff {
  added: BudgetLineDiffRow[];
  removed: BudgetLineDiffRow[];
  changed: BudgetLineDiffRow[];
  unchanged: number;
  matched_by: { entry_id: number; label_section: number };
  skipped: SkippedBudgetLine[];
}

/** One entry of `cells.entries` — WP-54's diffVersionCells change model (§8.6), consumed unchanged. */
export interface BudgetCellDiffRow {
  seq: number;
  sheet: string;
  cell_ref: string;
  old_value: string | null;
  new_value: string | null;
  old_formula: string | null;
  new_formula: string | null;
  reason: string;
  source: string;
  decided_by: string | null;
  decided_at: string;
}

export interface BudgetCellsDiff {
  entries: BudgetCellDiffRow[];
  sheets_only_in_a: string[];
  sheets_only_in_b: string[];
  /** The true count before the 500-entry cap (G9). */
  total: number;
  truncated: boolean;
}

/** One row of `clauses.changed` — a path-keyed comparison of stored cta_clauses rows (§13.8 FACTS 6). */
export interface ClauseDiffRow {
  path: string;
  heading: { a: string | null; b: string | null };
  hash: { a: string | null; b: string | null };
  has_insertions: { a: boolean; b: boolean };
  has_deletions: { a: boolean; b: boolean };
  /** First 200 characters, never the whole clause. */
  excerpt: { a: string; b: string };
}

export interface ClauseOneSided {
  path: string;
  heading: string | null;
  excerpt: string;
}

export interface ClausesDiff {
  added: ClauseOneSided[];
  removed: ClauseOneSided[];
  changed: ClauseDiffRow[];
  unchanged: number;
  /** A path repeated within one version (ledger [OWNER] 23) — never silently collapsed. */
  ambiguous_paths: string[];
}

export interface VersionDiffResult {
  kind: VersionDiffKind;
  a: VersionDiffSummarySide;
  b: VersionDiffSummarySide;
  lines: BudgetLinesDiff | null;
  cells: BudgetCellsDiff | null;
  clauses: ClausesDiff | null;
  labels: Record<string, string>;
  not_compared: string[];
}

/** GET /versions/:a/diff/:b. */
export async function getVersionDiff(a: string, b: string): Promise<VersionDiffResult> {
  const response = await api.get<VersionDiffResult>(`/api/contracts/versions/${a}/diff/${b}`);
  return response.data;
}

// ---------------------------------------------------------------------------
// The Word redline (WP-64, §9.4, R34) — GET /diff?a&b returns the .docx
// BYTES with four X-Contracts-Diff-* count headers, never JSON. Called only
// for a `kind: 'contract'` pair, as a download.
// ---------------------------------------------------------------------------

export interface RedlineDiffSummary {
  changed: number;
  inserted: number;
  deleted: number;
  refused: number;
}

export interface ContractRedlineDiff {
  bytes: ArrayBuffer;
  contentType: string;
  filename: string | null;
  summary: RedlineDiffSummary;
}

function filenameFromContentDisposition(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const starMatch = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (starMatch) {
    try {
      return decodeURIComponent(starMatch[1]);
    } catch {
      // fall through to the plain form below
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(value);
  return plainMatch ? plainMatch[1] : null;
}

/** GET /diff?a&b (WP-64, Appendix A "Versions"). axios lower-cases header keys. */
export async function getContractRedlineDiff(a: string, b: string): Promise<ContractRedlineDiff> {
  const response = await api.get('/api/contracts/diff', {
    params: { a, b },
    responseType: 'arraybuffer',
  });
  const headers = response.headers as Record<string, string>;
  return {
    bytes: response.data as ArrayBuffer,
    contentType: headers['content-type'] ?? 'application/octet-stream',
    filename: filenameFromContentDisposition(headers['content-disposition']),
    summary: {
      changed: Number(headers['x-contracts-diff-changed'] ?? 0),
      inserted: Number(headers['x-contracts-diff-inserted'] ?? 0),
      deleted: Number(headers['x-contracts-diff-deleted'] ?? 0),
      refused: Number(headers['x-contracts-diff-refused'] ?? 0),
    },
  };
}

// ---------------------------------------------------------------------------
// Mark executed / supersede (WP-32, mounted) — no frontend function existed
// for either mounted route before this WP.
// ---------------------------------------------------------------------------

export interface MarkExecutedResult {
  version: ContractsVersion;
  negotiation: unknown;
}

/** POST /versions/:id/executed — 409 with code CTA_DOCUMENTS_OUTCOME_NOTE_REQUIRED when the negotiation has no outcome note yet (D19). */
export async function markVersionExecuted(versionId: string): Promise<MarkExecutedResult> {
  const response = await api.post<MarkExecutedResult>(`/api/contracts/versions/${versionId}/executed`);
  return response.data;
}

export interface SupersedeVersionInput {
  direction?: string;
  round?: number | null;
  versionDate?: string;
  source?: string;
}

export interface SupersedeVersionResult {
  version: ContractsVersion;
  supersedes: ContractsVersion;
}

/** POST /versions/:id/supersede — metadata only (§4.5); the old row is never written. */
export async function supersedeVersion(versionId: string, patch: SupersedeVersionInput): Promise<SupersedeVersionResult> {
  const response = await api.post<SupersedeVersionResult>(`/api/contracts/versions/${versionId}/supersede`, patch);
  return response.data;
}

// ---------------------------------------------------------------------------
// Loud failure (G13) — api/workspace.ts's describeWorkspaceApiError pattern,
// copied rather than imported (this WP owns none of workspace.ts's names).
// ---------------------------------------------------------------------------

interface ApiErrorLike {
  response?: { status?: number; data?: { error?: string } };
}

export function describeVersionsApiError(method: string, path: string, err: unknown): string {
  const apiErr = err as ApiErrorLike | undefined;
  const status = apiErr?.response?.status;
  const serverMessage = apiErr?.response?.data?.error;
  if (status === 404) {
    return `${method} ${path} — 404: ${
      typeof serverMessage === 'string' && serverMessage.length > 0 ? serverMessage : 'not mounted yet, or no such record'
    }.`;
  }
  if (typeof serverMessage === 'string' && serverMessage.length > 0) return serverMessage;
  return `${method} ${path} failed.`;
}
